/**
 * Pakṣa (lunar fortnight) detection that is robust to Unicode normalization.
 *
 * The API emits Sanskrit pakṣa names in NFD (decomposed) form — e.g. "Śukla" as
 * `S` + U+0301 (combining acute) + `ukla`, and "Kṛṣṇa" as `K` + `r` + U+0323 … —
 * so a naive /śukla/ or /kṛṣṇa/ regex written with precomposed characters
 * silently fails to match, flipping the waxing/waning decision (the moon glyph
 * then renders with the lit limb on the wrong side). We fold Latin combining
 * diacritics away before matching, and also accept the native-script names used
 * by the non-English locales (Devanagari / Telugu / Kannada).
 */

/** Strip Latin combining diacritics (U+0300–U+036F) and lowercase. */
const foldLatin = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** True when the pakṣa is Kṛṣṇa (waning fortnight). */
export function isKrishnaPaksha(paksha: string | null | undefined): boolean {
  if (!paksha) return false;
  if (/krsna|krishna/.test(foldLatin(paksha))) return true;
  return /कृष्ण|కృష్ణ|ಕೃಷ್ಣ/.test(paksha); // Devanagari / Telugu / Kannada
}

/** True when the pakṣa is Śukla (waxing fortnight). */
export function isShuklaPaksha(paksha: string | null | undefined): boolean {
  if (!paksha) return false;
  if (/sukla|shukla|waxing/.test(foldLatin(paksha))) return true;
  return /शुक्ल|శుక్ల|ಶುಕ್ಲ/.test(paksha);
}

/**
 * True when the moon is waxing (Śukla pakṣa). Falls back to "not Kṛṣṇa" so a
 * pakṣa string in any form still resolves to one of the two fortnights.
 */
export function isWaxingPaksha(paksha: string | null | undefined): boolean {
  if (isShuklaPaksha(paksha)) return true;
  if (isKrishnaPaksha(paksha)) return false;
  return true;
}
